import { ParticipantRole } from '@/consts/meeting';

import './participantsView.scss';
import ParticipantsSection, { ParticipantsList } from '@/components/ParticipantsSection';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useEffect, useState, useRef, useMemo } from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { Skeleton } from 'primereact/skeleton';
import { useNavigate } from 'react-router';
import type { Participant } from 'livekit-client';

import clsx from 'clsx';
import {
  useDisconnectButton,
  useLocalParticipant,
  useParticipantAttribute,
  useRoomContext,
  useTrackToggle,
  useRemoteParticipants,
  useParticipants
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { InputText } from 'primereact/inputtext';
import { useStudyModeStore } from '@/stores/studyMode.ts';
import { showToast } from '@/components/ToastManager';
import { useUserStore } from '@/stores/user.ts';
import api from '@/api/api';
import { useMeetingStore } from '@/stores/meeting.ts';
import { useBoardStore } from '@/stores/board.ts';
import { useSend } from '@/components/SendProvider';
import { useBoardPdfStore } from '@/stores/boardPdf';
import { DocProps } from '@/types/api';
import PdfWorker from '@/components/PdfWorker';

import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';

import { useMessagesStore } from '@/stores/messages';
import { useMeetingSettingsStore } from '@/stores/meetingSettings';

interface MeetingSiderProps {
  assistantTokenForInvite: string | null;
  loading: boolean;
}

interface Room {
  id: number;
  title: string;
  isEditing?: boolean;
}

interface CreateRoom {
  title: string;
}

interface RoomsListProps {
  rooms: Room[];
  handUpStatus: boolean;
  onEditRoomClick: (roomId: number) => void;
  onConfirmEditClick: (newRoom: Room) => void;
  onCancelEditClick: () => void;
  onConfirmAddRoomClick: (newRoom: CreateRoom) => void;
}

type HandUpdateMessage = {
  action: 'hand-up' | 'hand-down' | 'hand-up-in-room' | 'hand-down-in-room';
  studentId: string;
};

type CreateOrEditRoomMessage = {
  action: 'create-room' | 'edit-room';
  newRoom: Room;
};

type TransferToExitFromRoom = {
  action: 'transfer-student-to-exit-from-room';
  studentsId: string[];
};

interface Option {
  label: string;
  value: string;
}

function RoomsList({
  rooms,
  handUpStatus,
  onEditRoomClick,
  onConfirmEditClick,
  onCancelEditClick,
  onConfirmAddRoomClick
}: RoomsListProps) {
  const [newRoomTitle, setNewRoomTitle] = useState('');
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const role = useUserStore((state) => state.role);
  const { settings } = useMeetingSettingsStore();
  const { handUpRooms, setHandUpRooms, setHandUpStatus } = useMeetingStore.getState();
  const { meeting } = useMeetingStore.getState();
  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);
  const { sendStudentStatusUpdate } = useSend();
  const userUid = useUserStore((state) => state.userUid);

  const remoteParticipants = useRemoteParticipants();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const localParticipantRoomId = useParticipantAttribute('roomId', {
    participant: localParticipant
  });

  const activeRoomIds = useMemo(() => {
    return [
      ...new Set(participants.map((p) => p.attributes?.roomId).filter((id) => id !== undefined))
    ];
  }, [participants]);

  const activeRoomsQueue = handUpRooms.filter((id) => activeRoomIds.includes(id));

  const prevQueueRef = useRef<string[]>([]);

  useEffect(() => {
    if (!meeting?.uuid) return;

    const isQueueChanged = prevQueueRef.current.join(',') !== activeRoomsQueue.join(',');

    if (!isQueueChanged) return;

    const leaderIdentity = participants.map((p) => p.identity).sort()[0];
    const isLeader = userUid === leaderIdentity;

    const removedRooms = handUpRooms.filter((id) => !activeRoomsQueue.includes(id));

    if (isLeader && removedRooms.length > 0) {
      removedRooms.forEach((roomId) => {
        api.handDownInRoom(meeting?.uuid, Number(roomId));
      });
    }

    setHandUpRooms(activeRoomsQueue);
    prevQueueRef.current = activeRoomsQueue;
  }, [activeRoomsQueue, participants, meeting?.uuid, userUid, handUpRooms, setHandUpRooms]);

  const { sendTransferToExitFromRoom } = useSend();

  const addRoomClick = () => {
    setIsCreatingRoom(true);
    setNewRoomTitle('');
  };

  const resetAddClick = () => {
    setIsCreatingRoom(false);
    setNewRoomTitle('');
  };

  const confirmAddClick = () => {
    onConfirmAddRoomClick({ title: newRoomTitle.trim() });
    resetAddClick();
  };

  const editRoomClick = (room: Room) => {
    setNewRoomTitle(room.title);
    onEditRoomClick(room.id);
  };

  const confirmEditClick = (room: Room) => {
    onConfirmEditClick({
      ...room,
      title: newRoomTitle.trim(),
      isEditing: false
    });
  };

  const joinRoom = async (roomId: number) => {
    if (!meeting?.uuid) return;
    await localParticipant.setAttributes({ roomId: roomId.toString() });
    if (handUpRooms.includes(roomId.toString())) {
      if (role !== ParticipantRole.STUDENT) {
        setHandUpRooms((prevRooms) => prevRooms.filter((room) => room !== roomId.toString()));
        const message: HandUpdateMessage = {
          action: 'hand-down-in-room',
          studentId: roomId.toString()
        };
        sendStudentStatusUpdate(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
        await api.handDownInRoom(meeting?.uuid, roomId);
      } else {
        setHandUpStatus(true);
      }
    }
  };

  const leaveRoom = async (roomId: number) => {
    if (!meeting?.uuid) return;
    await localParticipant.setAttributes({ roomId: '' });
    let otherTutorsInRoom: boolean = false;
    let otherParticipantsInRoom: boolean = false;
    const studentsInRoom: Set<string> = new Set();
    for (const p of remoteParticipants) {
      if (Number(p.attributes?.roomId) === roomId) {
        otherParticipantsInRoom = true;
        if (p.attributes.role !== ParticipantRole.STUDENT) {
          otherTutorsInRoom = true;
          break;
        } else {
          studentsInRoom.add(p.identity);
        }
      }
    }
    if (role !== ParticipantRole.STUDENT && settings.autoExitWithTeacherFromRoom) {
      if (!otherTutorsInRoom) {
        const message: TransferToExitFromRoom = {
          action: 'transfer-student-to-exit-from-room',
          studentsId: [...studentsInRoom]
        };
        sendTransferToExitFromRoom(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
      }
    }
    if (!otherParticipantsInRoom) {
      setHandUpRooms((prevRooms) => prevRooms.filter((room) => room !== roomId.toString()));
      const message: HandUpdateMessage = {
        action: 'hand-down-in-room',
        studentId: roomId.toString()
      };
      sendStudentStatusUpdate(new TextEncoder().encode(JSON.stringify(message)), {
        reliable: true
      });
      await api.handDownInRoom(meeting?.uuid, roomId);
    }
    setHandUpStatus(false);
  };

  const sortedRooms = useMemo(() => {
    return [...rooms].sort((a, b) => {
      const idA = a.id.toString() ?? '';
      const idB = b.id.toString() ?? '';

      const indexA = activeRoomsQueue.indexOf(idA);
      const indexB = activeRoomsQueue.indexOf(idB);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return idA.localeCompare(idB, undefined, { numeric: true });
    });
  }, [rooms, activeRoomsQueue]);

  return (
    <div className="rooms">
      {sortedRooms.map((room) => {
        const displayIndex = activeRoomsQueue.indexOf(room.id.toString());
        const roomNumber = displayIndex !== -1 ? displayIndex + 1 : null;
        return (
          <div key={room.id}>
            <div className="room">
              <i className="pi pi-volume-up room__icon"></i>
              {room.isEditing ? (
                <div>
                  <InputText
                    autoFocus
                    className="room__edit-input"
                    value={newRoomTitle}
                    onChange={(e) => setNewRoomTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmEditClick(room);
                      if (e.key === 'Escape') onCancelEditClick();
                    }}
                  />
                  <Button
                    className="room__edit-button room__edit-button_confirm"
                    icon="pi pi-check"
                    disabled={!newRoomTitle.length}
                    onClick={() => confirmEditClick(room)}
                  />
                  <Button
                    className="room__edit-button room__edit-button_cancel"
                    icon="pi pi-times"
                    onClick={onCancelEditClick}
                  />
                </div>
              ) : (
                <>
                  {role !== ParticipantRole.STUDENT ||
                  (isPracticeMode && settings.canStudentsSelfTransitionToRooms) ? (
                    !roomNumber ? (
                      <span
                        className={clsx('room__title', handUpStatus && 'no-active')}
                        onClick={() =>
                          (role !== ParticipantRole.STUDENT || !handUpStatus) && joinRoom(room.id)
                        }
                      >
                        {room.title}
                      </span>
                    ) : (
                      <span
                        className={clsx('room__hand-up students active')}
                        onClick={() =>
                          (role !== ParticipantRole.STUDENT || !handUpStatus) && joinRoom(room.id)
                        }
                      >
                        <div className={clsx('room__hand-up_block students active')}>
                          <div className={clsx('room__hand-up_label active')}>
                            {role === ParticipantRole.STUDENT
                              ? `В очереди: ${roomNumber}`
                              : `Помочь: ${roomNumber}`}
                          </div>
                          <div className="room__hand-up_title">{room.title}</div>
                        </div>
                      </span>
                    )
                  ) : (
                    <span className={clsx('room__title', 'no-active')}>{room.title}</span>
                  )}
                  {role === ParticipantRole.TEACHER && (
                    <i
                      className="pi pi-pencil room__icon room__icon_edit"
                      title="Изменить название"
                      onClick={() => editRoomClick(room)}
                    ></i>
                  )}
                  {localParticipantRoomId === room.id.toString() && (
                    <i
                      className="pi pi-arrow-circle-right room__icon room__icon_leave"
                      title="Покинуть комнату"
                      onClick={() => leaveRoom(room.id)}
                    ></i>
                  )}
                </>
              )}
            </div>
            <ParticipantsList roomId={room.id} />
          </div>
        );
      })}
      {isCreatingRoom ? (
        <div>
          <InputText
            autoFocus
            className="room__edit-input"
            value={newRoomTitle}
            onChange={(e) => setNewRoomTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmAddClick();
              if (e.key === 'Escape') resetAddClick();
            }}
          />
          <Button
            className="room__edit-button room__edit-button_confirm"
            icon="pi pi-check"
            disabled={!newRoomTitle.length}
            onClick={confirmAddClick}
          />
          <Button
            className="room__edit-button room__edit-button_cancel"
            icon="pi pi-times"
            onClick={resetAddClick}
          />
        </div>
      ) : (
        role === ParticipantRole.TEACHER && (
          <Button
            className="rooms__add-room-button"
            icon="pi pi-plus-circle"
            size="small"
            onClick={addRoomClick}
          >
            добавить комнату
          </Button>
        )
      )}
    </div>
  );
}

function MeetingSiderSkeleton() {
  return (
    <div className="sider__skeleton sider__main">
      <Skeleton width="50%" height="1.5rem" className="sider__meeting-name"></Skeleton>
      <Skeleton width="90%" height="1rem" className="mb-2"></Skeleton>
      <Skeleton width="70%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="90%" height="1rem" className="mb-2"></Skeleton>
      <Skeleton width="60%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="70%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="90%" height="1rem" className="mb-2"></Skeleton>
      <Skeleton width="50%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="55%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="60%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="65%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="90%" height="1rem" className="mb-2"></Skeleton>
      <Skeleton width="45%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="55%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="65%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="30%" height="1.25rem" className="mb-2"></Skeleton>
      <Skeleton width="40%" height="1.25rem" className="mb-2"></Skeleton>
    </div>
  );
}

type ExportStage = 'idle' | 'boards' | 'docs' | 'completed';

const STAGE_TITLES: Record<ExportStage, string> = {
  idle: 'Проверка наличия файлов для сохранения...',
  boards: 'Сохранение досок...',
  docs: 'Сохранение документов в PDF...',
  completed: 'Все данные сохранены!'
};

interface ExportOverlayProps {
  visible: boolean;
  currentCount: number;
  total: number;
  stage: ExportStage;
}

export function ExportOverlay({ visible, currentCount, total, stage }: ExportOverlayProps) {
  const isCompletedStage = stage === 'completed';
  const progressValue = isCompletedStage
    ? 100
    : total > 0
      ? Math.round((currentCount / total) * 100)
      : 0;

  return (
    <Dialog
      visible={visible}
      onHide={() => {}}
      closable={false}
      modal
      showHeader={false}
      className={clsx('export-dialog', progressValue === 0 && !isCompletedStage && 'no-transition')}
      closeOnEscape={false}
      dismissableMask={false}
      blockScroll={true}
    >
      <div className="export-dialog container">
        <h5 className="export-dialog title">{STAGE_TITLES[stage]}</h5>

        <ProgressBar value={progressValue} displayValueTemplate={(value) => `${value}%`} />

        <p className="export-dialog status-text">
          {stage === 'boards'
            ? `Страница ${currentCount} из ${total}`
            : stage === 'docs'
              ? `Документ ${currentCount} из ${total}`
              : 'Готово'}
        </p>
      </div>
    </Dialog>
  );
}

function ParticipantsView({ assistantTokenForInvite, loading }: MeetingSiderProps) {
  const meeting = useMeetingStore((state) => state.meeting);
  const rooms = useMeetingStore((state) => state.rooms);
  const handUpStatus = useMeetingStore((state) => state.handUpStatus);
  const { resetMeeting } = useMeetingStore();
  const { resetFiles } = useBoardPdfStore();
  const { setRooms, addRoom, setHandUpStatus, setHandUpStudents } = useMeetingStore.getState();
  const { handUpRooms, handUpStudents, setHandUpRooms } = useMeetingStore.getState();
  const [isExiting, setIsExiting] = useState(false);
  const [exportQueue, setExportQueue] = useState<DocProps[]>([]);
  const [currentCountExportBoards, setCurrentCountExportBoards] = useState<number>(0);
  const [totalCountExportBoards, setTotalCountExportBoards] = useState<number>(0);
  const { settings } = useMeetingSettingsStore();

  const { uploadDocs } = useBoardPdfStore();

  const [totalToExport, setTotalToExport] = useState(0);
  const [exportStage, setExportStage] = useState<ExportStage>('idle');
  const { resetMessages } = useMessagesStore();

  const role = useUserStore((state) => state.role);
  const userUid = useUserStore((state) => state.userUid);

  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);
  const { toggleStudyMode, setStudyMode } = useStudyModeStore.getState();

  const currentUserEditor = useBoardStore((state) => state.currentUserEditor);

  const { enabled, toggle } = useTrackToggle({ source: Track.Source.Microphone });
  const { buttonProps: disconnectButtonProps } = useDisconnectButton({});
  const roleRef = useRef(role);

  const navigate = useNavigate();

  const [options, setOptions] = useState<Option[]>([]);

  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const localParticipantRoomId = useParticipantAttribute('roomId', {
    participant: localParticipant
  });

  const meetingUid = meeting?.uuid;
  const meetingTitle = meeting?.title || 'Встреча';
  const remoteParticipants = useRemoteParticipants();
  const [showTitleOfAssistants, setShowTitleOfAssistants] = useState<boolean>(false);
  const [showTitleOfStudents, setShowTitleOfStudents] = useState<boolean>(false);

  const { sendStudentStatusUpdate, sendCreateOrEditRoom } = useSend();

  const resolveRef = useRef<(() => void) | null>(null);

  const valueTemplate = () => {
    return (
      <span className="sider__invite-button-template">
        <img src="/meeting_icons/link.svg" />
        Пригласить
      </span>
    );
  };

  const onEditRoomClick = (roomId: number) => {
    setRooms(
      rooms.map((room) => {
        if (room.id !== roomId) {
          return { ...room, isEditing: false };
        }

        return {
          ...room,
          isEditing: true
        };
      })
    );
  };

  const onConfirmEditClick = async (newRoom: Room) => {
    try {
      if (meetingUid) {
        await api.editRoom(meetingUid, newRoom.id, { name: newRoom.title });
        setRooms(rooms.map((room) => (room.id === newRoom.id ? newRoom : room)));
        const message: CreateOrEditRoomMessage = {
          action: 'edit-room',
          newRoom: newRoom
        };
        sendCreateOrEditRoom(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
      }
    } catch (err) {
      console.error('Error editing room', err);
    }
  };

  const onCancelEditClick = () => setRooms(rooms.map((room) => ({ ...room, isEditing: false })));

  const onConfirmAddRoomClick = async (newRoom: CreateRoom) => {
    try {
      if (meetingUid) {
        const meetingRoom = await api.createRoom(meetingUid, { name: newRoom.title });
        const sendRoom = addRoom(meetingRoom.id, newRoom.title);
        const message: CreateOrEditRoomMessage = {
          action: 'create-room',
          newRoom: sendRoom
        };
        sendCreateOrEditRoom(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
      }
    } catch (err) {
      console.error('Error creating room', err);
    }
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const onDisconnectClick = async () => {
    if (currentUserEditor && meeting?.uuid && userUid) {
      try {
        setIsExiting(true);
        await new Promise((resolve) => requestAnimationFrame(resolve));
        const pages = currentUserEditor.getPages();
        let count = 0;
        for (const page of pages) {
          const shapeIds = currentUserEditor.getPageShapeIds(page.id);
          if (shapeIds.size !== 0) {
            count++;
          }
        }
        setTotalCountExportBoards(count);
        if (count !== 0) {
          const uploadPromises: Promise<void>[] = [];
          setExportStage('boards');
          for (const page of pages) {
            const shapeIds = currentUserEditor.getPageShapeIds(page.id);
            if (shapeIds.size === 0) continue;
            const uploadPromise = (async () => {
              try {
                const imageBlob = await currentUserEditor.toImage([...shapeIds], {
                  format: 'png',
                  scale: 1,
                  background: true
                });
                if (imageBlob) {
                  return await api.uploadBoard(meeting.uuid, userUid, imageBlob.blob, page.index);
                }
              } finally {
                setCurrentCountExportBoards((prev) => prev + 1);
              }
            })();
            uploadPromises.push(uploadPromise);
          }
          await Promise.all(uploadPromises);
          await sleep(800);
        }
        if (role === ParticipantRole.TEACHER) {
          if (uploadDocs.length > 0) {
            setExportStage('docs');
            setTotalToExport(uploadDocs.length);
            setIsExiting(true);

            const waitForExport = new Promise<void>((resolve) => {
              resolveRef.current = resolve;
              setExportQueue(uploadDocs);
            });

            await Promise.race([
              waitForExport,
              new Promise((resolve) => setTimeout(resolve, 30000))
            ]);

            setExportQueue([]);
            await sleep(800);
          }
        }
        if (count > 0 || uploadDocs.length > 0) {
          setExportStage('completed');
          await sleep(800);
        }
      } catch (e) {
        console.error('Failed to upload boards on disconnect:', e);
        // continue with disconnection even if upload fails
      } finally {
        resolveRef.current = null;
      }
    }

    try {
      if (role === ParticipantRole.TEACHER && meetingUid) {
        await api.authorDisconnect(meetingUid);
      } else if (meetingUid && userUid) {
        setHandUpStudents((prevStudents) => prevStudents.filter((student) => student !== userUid));
        const message: HandUpdateMessage = { action: 'hand-down', studentId: userUid };
        sendStudentStatusUpdate(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
        await api.handDown(meetingUid, userUid);
        await api.disconnectParticipant(meetingUid, userUid);
      }
      disconnectButtonProps.onClick();
    } finally {
      setIsExiting(false);
      resetMeeting();
      resetFiles();
      resetMessages();
      localStorage.removeItem('userId');
      navigate('/', {
        state: {
          finished_meeting_uid: meetingUid,
          user_uuid: userUid,
          role: role,
          finished_meeting_name: meetingTitle
        }
      });
    }
  };

  const confirmDisconnect = () => {
    const messageBeforeExit =
      role === ParticipantRole.TEACHER
        ? 'Вы действительно хотите закончить встречу?'
        : 'Вы действительно хотите покинуть встречу?';
    confirmDialog({
      message: messageBeforeExit,
      header: 'Подтверждение',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'accept',
      acceptLabel: 'Выйти',
      rejectLabel: 'Остаться',
      acceptClassName: 'accept-disconnect',
      rejectClassName: 'reject-disconnect',
      accept: onDisconnectClick
    });
  };

  const handleChange = async (e: { value: string }) => {
    try {
      await navigator.clipboard.writeText(e.value);
      if (e.value.includes('assistant')) {
        showToast('copyToast', 'info', 'Скопировано', 'Приглашение для ассистента скопировано');
      } else {
        showToast('copyToast', 'info', 'Скопировано', 'Приглашение для ученика скопировано');
      }
    } catch (error) {
      console.error('Error copying link for assistants', error);
    }
  };

  useEffect(() => {
    roleRef.current = role;
  }, [role]);

  useEffect(() => {
    if (!room || role === ParticipantRole.TEACHER || !meetingUid) return;

    const handleParticipantDisconnected = async (participant: Participant) => {
      const isTeacher = participant.identity === meeting.author_uuid;
      if (isTeacher) {
        const res = await api.checkRoom(meetingUid);
        if (res) {
          await onDisconnectClick();
        }
      }
    };

    room.on('participantDisconnected', handleParticipantDisconnected);

    return () => {
      room.off('participantDisconnected', handleParticipantDisconnected);
    };
  }, [room, role, meetingUid]);

  const studentInviteOption = {
    label: 'Пригласить ученика',
    value: `${window.location.origin}/meeting/${meetingUid}`
  };

  useEffect(() => {
    const getStudyMode = async (meetingUid: string | undefined) => {
      if (meetingUid) {
        await setStudyMode(meetingUid);
      }
    };
    getStudyMode(meetingUid);
    setOptions([studentInviteOption]);
  }, [meetingUid]);

  useEffect(() => {
    if (assistantTokenForInvite) {
      const optionAssistant = {
        label: 'Пригласить ассистента',
        value: `${window.location.origin}/meeting/assistant/${assistantTokenForInvite}`
      };
      setOptions([optionAssistant, studentInviteOption]);
    }
  }, [assistantTokenForInvite]);

  useEffect(() => {
    const assistantsExist = remoteParticipants.some(
      (p) => p.attributes.role === ParticipantRole.ASSISTANT
    );
    const studentsExist = remoteParticipants.some(
      (p) => p.attributes.role === ParticipantRole.STUDENT
    );
    setShowTitleOfAssistants(assistantsExist);
    setShowTitleOfStudents(studentsExist);
  }, [remoteParticipants]);

  useEffect(() => {
    if (!handUpRooms || !handUpStudents) return;
    if (
      (localParticipantRoomId && handUpRooms.includes(localParticipantRoomId)) ||
      handUpStudents.includes(userUid)
    ) {
      setHandUpStatus(true);
    } else {
      setHandUpStatus(false);
    }
  }, [handUpRooms, handUpStudents, localParticipantRoomId]);

  const handleHandUpToggle = async () => {
    if (
      !userUid ||
      !isPracticeMode ||
      !meetingUid ||
      (localParticipantRoomId && !settings.canStudentsHandUpInRooms)
    ) {
      return;
    }

    const newStatus = !handUpStatus;
    setHandUpStatus(newStatus);

    const actionHand = newStatus ? 'hand-up' : 'hand-down';
    if (newStatus) {
      if (localParticipantRoomId && !handUpRooms.includes(localParticipantRoomId)) {
        await api.handUpInRoom(meetingUid, Number(localParticipantRoomId));
        setHandUpRooms((prevRooms) => [...prevRooms, localParticipantRoomId]);
        const message: HandUpdateMessage = {
          action: `${actionHand}-in-room`,
          studentId: localParticipantRoomId
        };
        sendStudentStatusUpdate(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
      } else {
        await api.handUp(meetingUid, userUid);
        setHandUpStudents((prevStudents) => [...prevStudents, userUid]);
        const message: HandUpdateMessage = { action: actionHand, studentId: userUid };
        sendStudentStatusUpdate(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
      }
    } else {
      if (localParticipantRoomId && handUpRooms.includes(localParticipantRoomId)) {
        await api.handDownInRoom(meetingUid, Number(localParticipantRoomId));
        setHandUpRooms((prevRooms) => prevRooms.filter((room) => room !== localParticipantRoomId));
        const message: HandUpdateMessage = {
          action: `${actionHand}-in-room`,
          studentId: localParticipantRoomId
        };
        sendStudentStatusUpdate(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
      } else {
        await api.handDown(meetingUid, userUid);
        setHandUpStudents((prevStudents) => prevStudents.filter((student) => student !== userUid));
        const message: HandUpdateMessage = { action: actionHand, studentId: userUid };
        sendStudentStatusUpdate(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
      }
    }
  };

  const handleWorkerDone = (id: string) => {
    setExportQueue((prev) => {
      const next = prev.filter((docId) => docId.id !== id);

      if (next.length === 0 && resolveRef.current) {
        resolveRef.current();
      }

      return next;
    });
  };

  const currentProgressCount =
    exportStage === 'boards' ? currentCountExportBoards : totalToExport - exportQueue.length;

  const currentTotal = exportStage === 'boards' ? totalCountExportBoards : totalToExport;

  return (
    <>
      {loading ? (
        <MeetingSiderSkeleton />
      ) : (
        <>
          <ConfirmDialog className="custom-confirm-dialog" draggable={false} />
          <div className="sider__main">
            <h3 className="sider__meeting-name">{meetingTitle}</h3>
            <ParticipantsSection sectionTitle="Преподаватель" role="teacher" rooms={rooms} />
            {(showTitleOfAssistants || role === ParticipantRole.ASSISTANT) && (
              <ParticipantsSection sectionTitle="Ассистенты" role="assistant" rooms={rooms} />
            )}
            {(showTitleOfStudents || role === ParticipantRole.STUDENT) && (
              <ParticipantsSection sectionTitle="Ученики" role="student" rooms={rooms} />
            )}
            <p className="participants__section-title">
              <span>Комнаты</span>
            </p>
            <RoomsList
              rooms={rooms}
              handUpStatus={handUpStatus}
              onEditRoomClick={onEditRoomClick}
              onConfirmEditClick={onConfirmEditClick}
              onCancelEditClick={onCancelEditClick}
              onConfirmAddRoomClick={onConfirmAddRoomClick}
            />
          </div>
          <div className="sider__bottom">
            {role === ParticipantRole.TEACHER ? (
              <div className="sider__teacher-controls">
                <div className="sider__mode-switch">
                  <span className="sider__mode-label">теория</span>
                  <InputSwitch
                    className="sider__inputswitch"
                    checked={isPracticeMode}
                    onChange={() => meetingUid && toggleStudyMode(meetingUid)}
                    disabled={role !== ParticipantRole.TEACHER}
                  />
                  <span className="sider__mode-label">практика</span>
                </div>
                <Dropdown
                  className={clsx(
                    'sider__invite-button',
                    isPracticeMode ? 'sider__dropdown-true' : 'sider__dropdown-false'
                  )}
                  options={options}
                  onChange={handleChange}
                  placeholder="Пригласить"
                  valueTemplate={valueTemplate}
                />
              </div>
            ) : (
              <div className="sider__meeting-status">
                <span className="sider__current-mode-text">текущий режим:</span>
                <span className="sider__current-mode">
                  {isPracticeMode ? 'практика' : 'теория'}
                </span>
              </div>
            )}
            <div className="sider__controls">
              {role === ParticipantRole.STUDENT && (
                <i
                  className={clsx(
                    'sider__control-icon',
                    'sider__control-icon_hand',
                    localParticipantRoomId && !settings.canStudentsHandUpInRooms && 'no-active'
                  )}
                  onClick={handleHandUpToggle}
                >
                  {!isPracticeMode ||
                  (localParticipantRoomId && !settings.canStudentsHandUpInRooms) ? (
                    <img
                      src="/meeting_icons/back_hand_not_active.svg"
                      className="no-active"
                      alt="hand up"
                    />
                  ) : (
                    <img
                      src={
                        handUpStatus
                          ? '/meeting_icons/back_hand_up.svg'
                          : '/meeting_icons/back_hand.svg'
                      }
                      className={
                        handUpStatus
                          ? 'sider__control-icon_hand_up'
                          : 'sider__control-icon_hand_down'
                      }
                    />
                  )}
                </i>
              )}
              <i
                className={clsx(
                  'pi',
                  'pi-microphone',
                  'sider__control-icon',
                  !enabled && 'sider__control-icon_muted'
                )}
                onClick={() => toggle()}
              />
              <i
                className={clsx(
                  'pi',
                  'pi-sign-out',
                  'sider__control-icon',
                  'sider__control-icon_exit'
                )}
                onClick={confirmDisconnect}
              />
            </div>
          </div>
          <ExportOverlay
            visible={isExiting}
            currentCount={currentProgressCount}
            total={currentTotal}
            stage={exportStage}
          />
          {exportQueue.length > 0 && (
            <PdfWorker
              key={exportQueue[0].id}
              onDone={handleWorkerDone}
              currentExportDoc={exportQueue[0]}
            />
          )}
        </>
      )}
    </>
  );
}

export default ParticipantsView;
